import * as React from 'react';
import styled from 'styled-components';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';
import type { AppearanceProps } from '../../../types';
import { ContentPosition, Mode, Theme } from '../../../types';

const StageContentContainer = styled.div<{
  m: number;
  contentPosition: ContentPosition;
}>`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: ${({ contentPosition }) => {
    if (contentPosition === ContentPosition.BOTTOM_LEFT) {
      return 'flex-end';
    }
    if (
      contentPosition === ContentPosition.TOP_LEFT ||
      contentPosition === ContentPosition.TOP_RIGHT
    ) {
      return 'flex-start';
    }

    return '';
  }};

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    // Styles for mobile devices
    align-items: ${({ contentPosition }) => {
      if (contentPosition === ContentPosition.CENTER) {
        return 'center';
      }
      return '';
    }};
    justify-content: ${({ contentPosition }) => {
      if (contentPosition === ContentPosition.CENTER) {
        return 'center';
      }
      return '';
    }};
  }
  @media (max-width: ${SYS_BREAKPOINT_MD}) {
    // Styles for mobile devices
    align-items: ${({ contentPosition }) => {
      if (contentPosition === ContentPosition.CENTER) {
        return 'flex-end';
      }
      return '';
    }};
    justify-content: ${({ contentPosition }) => {
      if (contentPosition === ContentPosition.CENTER) {
        return '';
      }
      return '';
    }};
  }
`;

interface ShaderProps {
  background: string;
  enableShader: boolean;
  contentPosition?: ContentPosition;
  isModelPage?: boolean;
}

const ShaderBase = styled.div<ShaderProps>`
  pointer-events: none; // Make the container click-through
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
  text-align: ${({
    contentPosition,
    isModelPage,
  }: {
    contentPosition?: ContentPosition;
    isModelPage?: boolean;
  }) => {
    return contentPosition === ContentPosition.CENTER && !isModelPage ? 'center;' : 'left;';
  }};
`;

const ShaderTop = styled(ShaderBase)`
  background: ${({ background, enableShader }): string => (enableShader ? background : 'none')};
`;

const ShaderBottom = styled(ShaderBase)`
  background: ${({ background, enableShader }): string => (enableShader ? background : 'none')};
`;

export interface StageShaderProps {
  appearance: AppearanceProps;
  isModelPage?: boolean;
  isVideoStage?: boolean;
  children: React.ReactNode;
}

const HorizontalAlignmentDiv = styled.div<{ contentPosition?: ContentPosition }>`
  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    ${({ contentPosition }: { contentPosition?: ContentPosition }) =>
      contentPosition === ContentPosition.TOP_RIGHT &&
      `
      position: relative;
      width: 100%;
      text-align: right;
  `}
  }
`;

const StageShader: React.FC<StageShaderProps> = (props: StageShaderProps) => {
  const { appearance, isModelPage, children, isVideoStage = false } = props;
  const { theme, enableShader, contentPosition = ContentPosition.BOTTOM_LEFT, mode } = appearance;
  const _enableShader = enableShader && theme === Theme.Dark;

  const SHADER_CONFIG = {
    SMALL_MODE: {
      top: 'linear-gradient(180deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 50%)',
      bottom: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 47.9%, rgba(0, 0, 0, 0.60) 100%)',
    },
    LARGE_MODE: {
      top: 'linear-gradient(180deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 51.5%);',
      bottom: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 46.66%, #000 100%)',
    },
  } as const; // Properties of the SHADER_CONFIG object cannot be changed

  const shaderBackground =
    mode === Mode.LARGE || isModelPage ? SHADER_CONFIG.LARGE_MODE : SHADER_CONFIG.SMALL_MODE;
  const isVideoStagePositionBottom =
    isVideoStage && appearance.contentPosition === ContentPosition.BOTTOM_LEFT;

  // Render
  if (
    (contentPosition === ContentPosition.TOP_LEFT ||
      contentPosition === ContentPosition.TOP_RIGHT) &&
    !isModelPage
  ) {
    return (
      <ShaderTop
        background={shaderBackground.top}
        enableShader={_enableShader}
        contentPosition={contentPosition}
        data-testid="stage-shader-top"
      >
        <StageContentContainer contentPosition={contentPosition} m={+SYS_BREAKPOINT_MD}>
          <HorizontalAlignmentDiv
            contentPosition={contentPosition}
            data-testid="responsive-horizontal-alignment"
          >
            {children}
          </HorizontalAlignmentDiv>
        </StageContentContainer>
      </ShaderTop>
    );
  }
  // No support for ContentPosition.BOTTOM_RIGHT in the bottom shader
  return (
    <ShaderBottom
      background={shaderBackground.bottom}
      enableShader={!isVideoStagePositionBottom ? _enableShader : false}
      data-testid="stage-shader-bottom"
    >
      <StageContentContainer
        contentPosition={isModelPage ? ContentPosition.BOTTOM_LEFT : appearance.contentPosition}
        m={+SYS_BREAKPOINT_MD}
      >
        {children}
      </StageContentContainer>
    </ShaderBottom>
  );
};

export default StageShader;
